import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AnswerState } from './types';
import { RootState } from '../../../app/store';
import {
  Answer,
  AnswerApplicant,
  AnswerCreatePayloadRequest,
  AnswersCreateRequest,
  AnswersDestroyRequest,
  AnswersListRequest,
  AnswersUpdateRequest,
  AnswerUpdatePayloadRequest
} from '../../../generated/api';
import { getAnswersApiClient } from '../../../common/apiClients';
import { getMessageFromError } from '../../../common/utils/apiUtils';

const sliceName = 'questions';

export const fetchAnswersAsync = createAsyncThunk<Answer[], string | undefined>(
  `${sliceName}/fetchAnswers`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAnswersApiClient();
      const request: AnswersListRequest = { attemptId: arg };
      return await apiClient.answersList(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createAnswerAsync = createAsyncThunk<AnswerApplicant, AnswerCreatePayloadRequest>(
  `${sliceName}/createAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAnswersApiClient();
      const request: AnswersCreateRequest = { answerCreatePayloadRequest: arg };
      return await apiClient.answersCreate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const updateAnswerAsync = createAsyncThunk<AnswerApplicant, { id: number; data: AnswerUpdatePayloadRequest }>(
  `${sliceName}/updateAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAnswersApiClient();
      const request: AnswersUpdateRequest = { id: arg.id, answerUpdatePayloadRequest: arg.data };
      return await apiClient.answersUpdate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const destroyAnswerAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyAnswer`,
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const apiClient = await getAnswersApiClient();
      const request: AnswersDestroyRequest = { id: arg };
      await apiClient.answersDestroy(request);
      dispatch(removeAnswer(arg));
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const initialState: AnswerState = {
  answers: [],
  loading: false
};

const answerSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    removeAnswer: (state, action: PayloadAction<number>): AnswerState => {
      return { ...state, answers: state.answers.filter((answer) => answer.id !== action.payload) };
    },
    clearAnswers: (state): AnswerState => {
      return { ...state, answers: [] };
    }
  },
  extraReducers: {
    [fetchAnswersAsync.fulfilled.type]: (state, action: PayloadAction<Answer[]>): AnswerState => {
      return { ...state, answers: action.payload, loading: false };
    },
    [fetchAnswersAsync.pending.type]: (state): AnswerState => {
      return { ...state, loading: true };
    },
    [fetchAnswersAsync.rejected.type]: (state): AnswerState => {
      return { ...state, answers: [], loading: false };
    },
    [createAnswerAsync.fulfilled.type]: (state, action: PayloadAction<Answer>): AnswerState => {
      return { ...state, answers: [...state.answers, action.payload], loading: false };
    },
    [createAnswerAsync.pending.type]: (state): AnswerState => {
      return { ...state, loading: true };
    },
    [createAnswerAsync.rejected.type]: (state): AnswerState => {
      return { ...state, loading: false };
    },
    [updateAnswerAsync.fulfilled.type]: (state, action: PayloadAction<Answer>): AnswerState => {
      return {
        ...state,
        answers: state.answers.map((a) => (a.id === action.payload.id ? action.payload : a)),
        loading: false
      };
    },
    [updateAnswerAsync.pending.type]: (state): AnswerState => {
      return { ...state, loading: true };
    },
    [updateAnswerAsync.rejected.type]: (state): AnswerState => {
      return { ...state, loading: false };
    }
  }
});

export const selectAnswers = (state: RootState): AnswerState => state.answers;
export const { removeAnswer, clearAnswers } = answerSlice.actions;
export default answerSlice.reducer;
